import { isUndefined } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type GetMultipartPartResponse } from '@air/api/types';
import { NetworkStatusInfo } from '@air/classes-network-status-info';
import {
  type LargeUploadWithS3,
  setLargeUploadCancellablesAction,
  setLargeUploadChunkFinishedAction,
  setLargeUploadChunkStartedAction,
  setLargeUploadProgressAction,
  setUploadedPartInfo,
  setUploadInstabilityDetectedAction,
  setUploadUploadingAction,
  setUploadUrlTypeAction,
  type Upload,
  UploadStatus,
  type UploadUrlType,
  uploadByIdSelector,
  uploadedPartsSelector,
  uploadLogger,
  uploadProgressSelector,
  useUploaderStore,
} from '@air/redux-uploader';
import { type OnProgressParameter, uploadFile } from '@air/upload';

import { getUploadUrlType } from '../getUploadUrlForRetry';
import { isCurrentUploadSession } from '../isCurrentUploadSession';
import { isUploadTransferInstabilityError } from '../uploadErrors';

export type UploadPartErrorParams = {
  upload: Upload;
  partNumber: number;
  error: unknown;
  abortReason?: 'cancel' | 'pause' | 'transfer_aborted';
  retryCount?: number;
  maxRetryCount?: number;
  isOnline?: boolean;
  isRetryable?: boolean;
  uploadUrlType: UploadUrlType;
};

export type UploadPartStartedParams = {
  upload: Upload;
  partNumber: number;
  retryCount: number;
  maxRetryCount?: number;
  isOnline?: boolean;
  uploadUrlType: UploadUrlType;
};

export type UploadPartCompletedParams = UploadPartStartedParams & {
  partUploadDurationMs?: number;
};

export type UploadFailedParams = {
  upload: Upload;
  error: unknown;
  uploadUrlType?: Upload['uploadUrlType'];
};

export type UploadFileChunkParams = {
  upload: LargeUploadWithS3;
  partNumber: number;
  chunk: Blob;
  getPartUrl: (partNumber: number) => Promise<GetMultipartPartResponse>;
  onPartCompleted?: (params: UploadPartCompletedParams) => void;
  onPartError?: (params: UploadPartErrorParams) => void;
  onPartStarted?: (params: UploadPartStartedParams) => void;
  onUploadFailed?: (params: UploadFailedParams) => void;
  retryCount?: number;
  maxRetryCount?: number;
};

export const useUploadLargeFileChunk = () => {
  const store = useUploaderStore();
  const dispatch = useDispatch();

  const uploadLargeFileChunk = useCallback(
    async (params: UploadFileChunkParams) => {
      const { chunk, upload, partNumber, getPartUrl } = params;

      if (upload.status !== UploadStatus.uploading) {
        dispatch(setUploadUploadingAction({ uploadId: upload.id }));
      }

      const uploadedParts = uploadedPartsSelector(store.getState(), upload.id);
      const currentUpload = uploadByIdSelector(store.getState(), upload.id);

      const isPartAlreadyUploaded = uploadedParts.some(({ PartNumber }) => PartNumber === partNumber);
      const isPartAlreadyStarted = currentUpload?.activeChunks?.[partNumber];

      if (isPartAlreadyUploaded || isPartAlreadyStarted) {
        return;
      }

      dispatch(setLargeUploadChunkStartedAction({ partNumber, size: chunk.size, uploadId: upload.id }));

      try {
        const {
          s3Info,
          s3Info: {
            uploadUrlInfo: { mime },
          },
        } = upload;

        const partUploadUrlInfo = await getPartUrl(partNumber);
        const { url } = partUploadUrlInfo;
        const uploadUrlType = getUploadUrlType({ currentUrl: url, uploadUrlInfo: partUploadUrlInfo });
        let partUploadStartedAt: number | undefined;

        if (!isCurrentUploadSession({ currentUpload: uploadByIdSelector(store.getState(), upload.id), upload })) {
          return;
        }

        const _onProgress = ({ progress, abort }: OnProgressParameter) => {
          if (!isUndefined(progress)) {
            dispatch(setLargeUploadProgressAction({ abort, partNumber, progress, uploadId: upload.id }));
          }
        };

        const onUploadStart = (xhr: XMLHttpRequest) => {
          partUploadStartedAt = Date.now();

          uploadLogger.chunkStarted({ partNumber });
          dispatch(setUploadUrlTypeAction({ uploadId: upload.id, uploadUrlType }));
          params.onPartStarted?.({
            isOnline: NetworkStatusInfo.isOnline,
            maxRetryCount: params.maxRetryCount,
            partNumber,
            retryCount: params.retryCount ?? 0,
            upload,
            uploadUrlType,
          });
          dispatch(
            setLargeUploadCancellablesAction({
              uploadId: upload.id,
              uploadUrlInfo: s3Info.uploadUrlInfo,
              xhr: { [partNumber]: xhr },
            }),
          );
        };

        const { ETag } = await uploadFile({
          contentType: mime,
          file: chunk,
          onProgress: _onProgress,
          onStart: onUploadStart,
          returnTag: true,
          url,
        });

        uploadLogger.chunkCompleted({ eTag: ETag, partNumber });

        if (!ETag) {
          throw new Error('Missing ETag for upload');
        }

        const uploadedPart = { ETag, PartNumber: partNumber };

        if (!isCurrentUploadSession({ currentUpload: uploadByIdSelector(store.getState(), upload.id), upload })) {
          return;
        }

        const { progress: totalUploaded } = uploadProgressSelector(store.getState(), upload.id);

        dispatch(
          setUploadedPartInfo({
            totalBytesUploaded: totalUploaded,
            uploadedPart,
            uploadId: upload.id,
          }),
        );

        params.onPartCompleted?.({
          isOnline: NetworkStatusInfo.isOnline,
          maxRetryCount: params.maxRetryCount,
          partNumber,
          partUploadDurationMs: partUploadStartedAt ? Math.max(Date.now() - partUploadStartedAt, 0) : undefined,
          retryCount: params.retryCount ?? 0,
          upload,
          uploadUrlType,
        });
      } catch (e) {
        if (isUploadTransferInstabilityError(e)) {
          dispatch(setUploadInstabilityDetectedAction());
        }
        throw e;
      } finally {
        if (isCurrentUploadSession({ currentUpload: uploadByIdSelector(store.getState(), upload.id), upload })) {
          dispatch(setLargeUploadChunkFinishedAction({ partNumber, uploadId: upload.id }));
        }
      }
    },
    [dispatch, store],
  );

  return {
    uploadLargeFileChunk,
  };
};
